import { DeviceErrors } from "@minervaproject/avsubsystem-core";

import {
  PreclassCheckResultType,
  PreclassCheckType,
  PreclassTestNameType,
  PreclassTestOutcomeType,
  PreclassTestResultSeverityType,
  PreclassTestSpecificErrorsType,
} from "../../shared/types/preclass-check";

import { t } from "i18n/i18n";

// TODO: This links are from Flight. We should decide if we want to provide this information for Forum V.
// The links are not displayed right now.
const LINKS = {
  chromeAccess:
    "https://help.minervaproject.com/en/articles/4973556-enabling-your-camera-and-microphone-in-chrome",
  computerRequirements: "https://help.minervaproject.com/en/articles/4292732-computer-requirements",
  connectionIssues:
    "https://help.minervaproject.com/en/articles/4296298-how-to-get-the-fastest-internet-connection",
  externalServices: "https://help.minervaproject.com/en/articles/3783642-external-services",
  liveTechSupport:
    "https://help.minervaproject.com/en/articles/4296187-contacting-live-tech-support",
  microphoneIssues: "https://help.minervaproject.com/en/articles/4296318-common-microphone-issues",
  troubleShootingCamera:
    "https://help.minervaproject.com/en/articles/4296180-troubleshooting-issues-with-my-camera",
  troubleShootingMic:
    "https://help.minervaproject.com/en/articles/4296318-troubleshooting-issues-with-my-microphone",
  videoIssues: "https://help.minervaproject.com/en/articles/4296180-common-video-issues",
  minervaHelpUrl: "http://help.minervaproject.com",
  downloadChromeUrl: "https://www.google.com/chrome/",
};

const CAMERA_ISSUES_LINK = LINKS.videoIssues;
const CAMERA_IS_IN_USE_LINK = `${CAMERA_ISSUES_LINK}#h_23e49fd838`;
const CHROME_ACCESS_LINK = LINKS.chromeAccess;
const LIVE_TECH_SUPPORT_LINK = LINKS.liveTechSupport;
const TROUBLESHOOTING_CAMERA_LINK = LINKS.troubleShootingCamera;
const TROUBLESHOOTING_MIC_LINK = LINKS.troubleShootingMic;

function createInfoArrays() {
  // The entries in the arrays are ordered by priority. The first entry is the highest priority.
  // --------------------------
  // Progress messages
  // --------------------------
  const progressInfo = [
    {
      key: PreclassTestNameType.MIC_ACCESS_ALLOWED,
      message: t("av.pre_class_check_messages.mic_access_allowed.message"),
      action: t("av.pre_class_check_messages.mic_access_allowed.action"),
      alertKind: "info" as const,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    },
    {
      key: PreclassTestNameType.AUDIO_LEVEL,
      message: t("av.pre_class_check_messages.audio_level.message"),
      action: t("av.pre_class_check_messages.audio_level.action"),
      alertKind: "info" as const,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    },
    {
      key: PreclassTestNameType.CONNECTION,
      message: t("av.pre_class_check_messages.av_connection.message"),
      action: t("av.pre_class_check_messages.av_connection.action"),
      alertKind: "info" as const,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    },
  ];

  const progressInfoSystemCheck = [
    {
      key: PreclassTestNameType.MIC_ACCESS_ALLOWED,
      message: t("av.pre_class_check_messages.mic_access_allowed.message"),
      action: t("av.pre_class_check_messages.mic_access_allowed.action"),
      alertKind: "info" as const,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    },
    {
      key: PreclassTestNameType.AUDIO_LEVEL,
      message: t("av.pre_class_check_messages.audio_level.message"),
      action: t("av.pre_class_check_messages.audio_level.action"),
      alertKind: "info" as const,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    },
    {
      key: PreclassTestNameType.CONNECTION,
      message: t("av.system_check.messages.av_connection.message"),
      action: t("av.system_check.messages.av_connection.action"),
      alertKind: "info" as const,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    },
  ];

  // --------------------------
  // Error messages
  // --------------------------
  const errorInfo = [
    // Wrong browser
    {
      key: PreclassTestSpecificErrorsType.WRONG_BROWSER,
      message: t("av.pre_class_check_messages.wrong_browser.message"),
      action: t("av.pre_class_check_messages.wrong_browser.action"),
      link: LINKS.downloadChromeUrl,
      alertKind: "error" as const,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    },
    // AV connection fatal
    {
      key: PreclassTestSpecificErrorsType.AV_CONNECTION_FATAL,
      message: t("av.pre_class_check_messages.av_connection_fatal.message"),
      action: t("av.pre_class_check_messages.av_connection_fatal.action"),
      link: LIVE_TECH_SUPPORT_LINK,
      alertKind: "error" as const,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    },
    // AV connection retrying
    {
      key: PreclassTestSpecificErrorsType.AV_CONNECTION_RETRYING,
      message: t("av.pre_class_check_messages.av_connection_retrying.message"),
      action: t("av.pre_class_check_messages.av_connection_retrying.action"),
      link: LIVE_TECH_SUPPORT_LINK,
      alertKind: "error" as const,
      severity: PreclassTestResultSeverityType.NONSKIPPABLE,
    },
    // --------------------------
    // Audio errors
    // --------------------------
    {
      key: DeviceErrors.AUDIO_MAC_NOT_ALLOWED,
      message: t("av.pre_class_check_messages.audio_device.audio_mac_not_allowed.message"),
      action: t("av.pre_class_check_messages.audio_device.audio_mac_not_allowed.action"),
      link: TROUBLESHOOTING_MIC_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
    {
      key: DeviceErrors.AUDIO_WINDOWS_CAMERA_IN_USE,
      message: t("av.pre_class_check_messages.audio_device.audio_windows_camera_in_use.message"),
      action: t("av.pre_class_check_messages.audio_device.audio_windows_camera_in_use.action"),
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
    {
      key: DeviceErrors.AUDIO_NOT_ALLOWED,
      message: t("av.pre_class_check_messages.audio_device.audio_not_allowed.message"),
      action: t("av.pre_class_check_messages.audio_device.audio_not_allowed.action"),
      link: CHROME_ACCESS_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
    {
      key: DeviceErrors.AUDIO_NOT_FOUND,
      message: t("av.pre_class_check_messages.audio_device.audio_not_found.message"),
      action: t("av.pre_class_check_messages.audio_device.audio_not_found.action"),
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
    {
      key: DeviceErrors.AUDIO_FAIL,
      message: t("av.pre_class_check_messages.audio_device.audio_fail.message"),
      action: t("av.pre_class_check_messages.audio_device.audio_fail.action"),
      link: TROUBLESHOOTING_MIC_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
    // --------------------------
    // Audio detection test
    // --------------------------
    {
      key: PreclassTestSpecificErrorsType.AUDIO_LEVEL_NOT_DETECTED,
      message: t("av.pre_class_check_messages.audio_level_not_detected.message"),
      action: t("av.pre_class_check_messages.audio_level_not_detected.action"),
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      link: TROUBLESHOOTING_MIC_LINK,
      alertKind: "error" as const,
    },
    // --------------------------
    // Video errors
    // --------------------------
    {
      key: DeviceErrors.VIDEO_MAC_NOT_ALLOWED,
      message: t("av.pre_class_check_messages.video_device.video_mac_not_allowed.message"),
      action: t("av.pre_class_check_messages.video_device.video_mac_not_allowed.action"),
      link: TROUBLESHOOTING_CAMERA_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
    {
      key: DeviceErrors.VIDEO_WINDOWS_CAMERA_IN_USE,
      message: t("av.pre_class_check_messages.video_device.video_windows_camera_in_use.message"),
      action: t("av.pre_class_check_messages.video_device.video_windows_camera_in_use.action"),
      link: CAMERA_IS_IN_USE_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
    {
      key: DeviceErrors.VIDEO_NOT_ALLOWED,
      message: t("av.pre_class_check_messages.video_device.video_not_allowed.message"),
      action: t("av.pre_class_check_messages.video_device.video_not_allowed.action"),
      link: CHROME_ACCESS_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
    {
      key: DeviceErrors.VIDEO_NOT_FOUND,
      message: t("av.pre_class_check_messages.video_device.video_not_found.message"),
      action: t("av.pre_class_check_messages.video_device.video_not_found.action"),
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
    {
      key: DeviceErrors.VIDEO_FAIL,
      message: t("av.pre_class_check_messages.video_device.video_fail.message"),
      action: t("av.pre_class_check_messages.video_device.video_fail.action"),
      link: TROUBLESHOOTING_CAMERA_LINK,
      severity: PreclassTestResultSeverityType.SKIPPABLE,
      alertKind: "error" as const,
    },
  ];

  return {
    progressInfoArray: progressInfo,
    progressInfoSystemCheck,
    errorInfoArray: errorInfo,
  };
}

type MessageInfo = {
  key: string | PreclassTestNameType;
  message: string;
  action: string;
  alertKind: "info" | "error";
  severity: PreclassTestResultSeverityType;
  link?: string;
};

export class PreclassCheckMessages {
  private static instance: PreclassCheckMessages;

  private progressInfoArray: MessageInfo[];

  private progressInfoSystemCheck: MessageInfo[];

  private errorInfoArray: MessageInfo[];

  private constructor() {
    const { progressInfoArray, progressInfoSystemCheck, errorInfoArray } = createInfoArrays();
    this.progressInfoArray = progressInfoArray;
    this.progressInfoSystemCheck = progressInfoSystemCheck;
    this.errorInfoArray = errorInfoArray;
  }

  public static getInstance(): PreclassCheckMessages {
    if (!PreclassCheckMessages.instance) {
      PreclassCheckMessages.instance = new PreclassCheckMessages();
    }
    return PreclassCheckMessages.instance;
  }

  static getErrorMessageFromDeviceError(deviceError: DeviceErrors): string | null {
    const instance = PreclassCheckMessages.getInstance();
    const errorMessage = instance.errorInfoArray.find(
      (errorEntry) => String(errorEntry.key) === String(deviceError),
    );
    return errorMessage?.message || null;
  }

  static getHighestPriorityMessageFromResults(
    results: PreclassCheckResultType,
    preclassCheckType: PreclassCheckType = PreclassCheckType.PRECLASS,
  ) {
    const instance = PreclassCheckMessages.getInstance();
    // get a list of the errors in the PreclassCheckResultType
    const errors: string[] = [];
    for (const result of Object.values(results)) {
      const error = String(result.error);
      if (error) errors.push(error);
    }

    const errorSet = new Set(errors);
    // Find the first error message that matches
    const errorMessage = instance.errorInfoArray.find((errorEntry) => errorSet.has(errorEntry.key));
    if (errorMessage) return errorMessage;

    const infoArray =
      preclassCheckType === PreclassCheckType.SYSTEM_CHECK
        ? instance.progressInfoSystemCheck
        : instance.progressInfoArray;
    // If no error, find the first progress message that is in progress
    const progressMessage = infoArray.find(
      (infoEntry) => results[infoEntry.key]?.outcome === PreclassTestOutcomeType.IN_PROGRESS,
    );
    if (progressMessage) return progressMessage;

    // If nothing found, return null
    return null;
  }
}

export { LINKS };
